/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 5:29:50 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormControl } from "@mui/material";
import { ErrorTypography, OSAlert } from "ui-widgets";
import React from "react";
import { useOSAPIFormContext, useOSFormContext } from "ui-widgets/forms/providers";
import { OSFormInputProps } from "ui-widgets/forms/types";
import { HelperText } from "ui-widgets/typography/CustomTypography";

export const HelperTextInputWrapper = ({ helperText, children }) => (
  <>
    {children}
    {helperText ? (
      <FormControl>
        <HelperText>{helperText}</HelperText>
      </FormControl>
    ) : null}
  </>
);

export const FormInputCustomErrorWrapper = ({ children }) => {
  return (
    <FormControl>
      <OSAlert severity="error">
        <ErrorTypography>{children}</ErrorTypography>
      </OSAlert>
    </FormControl>
  );
};

interface InputWrapperProps<T> extends OSFormInputProps<T> {
  children: React.ReactNode;
}

export const CustomInputValidationErrorWrapper = <T,>(props: InputWrapperProps<T>) => {
  // Use this wrapper for custom fields that dont have a native way to display errors e.g. the popup field, the image field
  // Fields like the MUI text field display errors the props passed by react hook useForm i.e. error = true
  const { formState } = useOSFormContext<T>();

  // asserting errors type to any. TODO: handle type conflict between T and FieldErrors<T> since FieldErrors<T> is just T with additional error information
  const errors = formState.errors as any;

  return (
    <React.Fragment>
      {props.children}
      {errors?.[props.schema_field_key]?.message && (
        <FormInputCustomErrorWrapper>
          <React.Fragment>{errors?.[props.schema_field_key]?.message}</React.Fragment>
        </FormInputCustomErrorWrapper>
      )}
    </React.Fragment>
  );
};

export const APIFormInputErrorWrapper = <T,>(props: InputWrapperProps<T>) => {
  const useAPIFormReturn = useOSAPIFormContext();

  return (
    <React.Fragment>
      {props.children}
      {useAPIFormReturn?.apiDataSendErrors?.[props.schema_field_key]?.length > 0 && (
        <FormInputCustomErrorWrapper>
          <React.Fragment>
            {useAPIFormReturn?.apiDataSendErrors?.[props.schema_field_key]?.map(
              (e: object) => `${props.label ? props.label + ": " : ""}${JSON.stringify(e, null, 2)}`
            )}
          </React.Fragment>
        </FormInputCustomErrorWrapper>
      )}
    </React.Fragment>
  );
};
